import type {
  ChatBlock,
  ChatBlocked,
  ChatCardKind,
  ChatMessage,
  ChatPerson,
  StageCohost,
  StageFace,
  StageHostRef,
  StageKind,
  StageLive,
  StagePayload,
  WatchCamera,
  WatchRoomState,
  WatchSourceRole,
  WatchTab,
} from './types'
import { parseStageGraphics } from './graphics'

type RawFace = { identity?: unknown; user_id?: unknown; role?: unknown; handle?: unknown; name?: unknown; studio?: unknown }

function face(raw: unknown): StageFace | null {
  if (!raw || typeof raw !== 'object') return null
  const { identity, user_id: userId, role, handle, name, studio } = raw as RawFace
  if (typeof identity !== 'string' || typeof userId !== 'string') return null
  return {
    identity,
    userId,
    role: role === 'guest' ? 'guest' : 'host',
    handle: typeof handle === 'string' ? handle : null,
    name: typeof name === 'string' ? name : null,
    studio: studio === true,
  }
}

const stageKind = (raw: unknown): StageKind | null => (raw === 'organization' || raw === 'person' ? raw : null)

function hostRef(raw: unknown): StageHostRef | null {
  if (!raw || typeof raw !== 'object') return null
  const { handle, name } = raw as { handle?: unknown; name?: unknown }
  const ref = { handle: typeof handle === 'string' && handle ? handle : null, name: typeof name === 'string' ? name : null }
  return ref.handle || ref.name ? ref : null
}

const userIdOf = (raw: unknown): string | null => (typeof raw === 'string' && raw ? raw : null)

/**
 * Badger asked onto a stage, as room metadata and the stage payload carry it (`cohost: {invited_by, since}`): any object
 * is a request, its fields read defensively (a blank or garbled field is null); null, absent (an API from before him)
 * or anything else is none.
 */
export function parseCohost(raw: unknown): StageCohost | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const { invited_by: invitedBy, since } = raw as { invited_by?: unknown; since?: unknown }
  return {
    invitedBy: typeof invitedBy === 'string' && invitedBy ? invitedBy : null,
    since: typeof since === 'string' && Number.isFinite(Date.parse(since)) ? since : null,
  }
}

/**
 * Anything a URL parser drops or reads as a slash: tabs and line breaks (and every other control character) are
 * stripped, so `/\t/host` is `//host`, and a backslash is a slash, so `/\host` is too.
 */
const URL_TRICKS = /[\u0000-\u001F\u007F\\]/

/** A page on this site (`/e/race/@shane`), never another origin's (`https://…`, `//…`): a stage's path is navigated to. */
const sitePath = (raw: unknown): string | null =>
  typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') && !URL_TRICKS.test(raw) ? raw : null

/**
 * The room metadata WatchRoomMetadata publishes. Old metadata (featured camera only, which API pods from
 * before stages keep writing during a rolling deploy) reads as `stages: null`, no stage state, not as empty stages,
 * and metadata from before the world layer as `worldVersion: null`.
 * Each stage says whose it is (`kind`, `host`, `owner_user_id`, since stage invites) and where its page is (`path`: every
 * event's page is the watch screen, though an older pod's may still carry a retired `?watch=1`, which `/e/` drops); a
 * stage from a pod before them reads those as null.
 */
export function parseRoomMetadata(raw: string | null | undefined): WatchRoomState | null {
  if (!raw) return null
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return null
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const record = data as { featured_participant_identity?: unknown; stages?: unknown; world_version?: unknown }
  let stages: Record<string, StageLive> | null = null
  if (record.stages && typeof record.stages === 'object') {
    stages = {}
    type RawStage = {
      called_identity?: unknown
      members?: unknown
      kind?: unknown
      host?: unknown
      owner_user_id?: unknown
      path?: unknown
      graphics?: unknown
      source_volume?: unknown
      cohost?: unknown
    }
    for (const [id, value] of Object.entries(record.stages as Record<string, RawStage | null>)) {
      stages[id] = {
        calledIdentity: typeof value?.called_identity === 'string' ? value.called_identity : null,
        members: Array.isArray(value?.members) ? value.members.map(face).filter((m): m is StageFace => m !== null) : [],
        kind: stageKind(value?.kind),
        host: hostRef(value?.host),
        ownerUserId: userIdOf(value?.owner_user_id),
        path: sitePath(value?.path),
        // Missing (an API pod from before broadcast elements) or `{}`: nothing on screen.
        graphics: parseStageGraphics(value?.graphics),
        sourceVolume: percentOf(value?.source_volume),
        // Absent (an API pod from before Badger) or null: not asked for.
        cohost: parseCohost(value?.cohost),
      }
    }
  }
  const worldVersion = record.world_version
  return {
    featured: typeof record.featured_participant_identity === 'string' ? record.featured_participant_identity : null,
    stages,
    worldVersion: typeof worldVersion === 'number' && Number.isInteger(worldVersion) && worldVersion >= 0 ? worldVersion : null,
  }
}

/** The loaded stage payload as a live stage, until the room's metadata takes over. */
export function stageFromPayload(stage: StagePayload['stage']): StageLive {
  return {
    calledIdentity: typeof stage.called_identity === 'string' ? stage.called_identity : null,
    members: Array.isArray(stage.members) ? stage.members.map(face).filter((m): m is StageFace => m !== null) : [],
    kind: stageKind(stage.kind),
    host: hostRef(stage.host),
    ownerUserId: userIdOf(stage.owner_user_id),
    // Until the room's metadata takes over (it wins once it carries stages); none from an API before broadcast elements.
    ...(stage.graphics !== undefined ? { graphics: parseStageGraphics(stage.graphics) } : {}),
    sourceVolume: percentOf(stage.source_volume),
    // Until the room's metadata takes over, as the call; none from an API before Badger.
    cohost: parseCohost(stage.cohost),
  }
}

/** A whole percentage from 0 to 100, as the API sends a stage's source volume; left out for anything else. */
function percentOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100 ? value : undefined
}

/**
 * The stage a watch URL names after its event: `/e/:shortcode/@<handle>` is that person's stage (the handle, without
 * the @); `/e/:shortcode`, or anything else after it, is the owner's (null). `rest` is the route's `*`.
 */
export function stageHandleFromPath(rest: string | null | undefined): string | null {
  const first = (rest ?? '').split('/')[0]
  if (!first.startsWith('@')) return null
  const handle = first.slice(1).trim()
  return handle || null
}

/** Handles name the same person whatever their case, as the API matches them (`LOWER(users.username)`). */
export function sameHandle(a: string | null | undefined, b: string | null | undefined): boolean {
  return Boolean(a && b && a.toLowerCase() === b.toLowerCase())
}

/**
 * Where a stage's page is: the API's `path` when it sent one (every event's page is the watch screen; an older
 * server's may still carry a retired `?watch=1`, which `/e/` drops), else `/e/<shortcode>` for the owner's stage
 * (`handle` null) or `/e/<shortcode>/@<handle>` for a person's. A path without a query keeps `search`, the page's own.
 * Only a path on this site: anything else is made from the shortcode and handle.
 */
export function stagePath({ path, shortcode, handle, search = '' }: { path?: string | null; shortcode: string; handle: string | null; search?: string }): string {
  const base = sitePath(path) ?? (handle ? `/e/${shortcode}/@${handle}` : `/e/${shortcode}`)
  return base.includes('?') ? base : `${base}${search}`
}

/**
 * A stage as the switcher names it: "Shane’s stage", then ", live" while someone on it is in the room, and how many
 * are ("2 on stage"). `present` counts the seats whose participant is in the connected room (a seat can outlive its tab).
 */
export function stageLabel(name: string, present: number): string {
  const stage = `${visibleText(name) || name}’s stage`
  return present > 0 ? `${stage}, live, ${present} on stage` : stage
}

const EMPTY_STAGE: StageLive = { calledIdentity: null, members: [] }

/** A stage the metadata doesn't list yet, keeping whose it is from the loaded payload (so its owner's face still leads). */
const emptyLike = (loaded: StageLive): StageLive =>
  loaded.kind || loaded.ownerUserId ? { ...EMPTY_STAGE, kind: loaded.kind ?? null, host: loaded.host ?? null, ownerUserId: loaded.ownerUserId ?? null } : EMPTY_STAGE

/**
 * The stage's live state: the room's once its metadata carries stages, the loaded payload before that and
 * while old pods still write featured-only metadata (which would otherwise blank the faces). A stage the
 * metadata doesn't list yet (a fresh stage before its first publish) is empty.
 */
export function stageLive(room: WatchRoomState | null, stageId: string, loaded: StageLive): StageLive {
  if (!room?.stages) return loaded
  return room.stages[stageId] ?? emptyLike(loaded)
}

/**
 * Which camera fills the canvas: the viewer's pick; then the host's called camera while a host is live on the stage;
 * then a live Live show (the produced programme is the default view); then a call left on a stage nobody is hosting;
 * then the producer's featured camera; then the first online camera in `cameraOrder` (whatever order `cameras` comes
 * in). Each choice counts only while its camera is online. A Studio face (`faces`) counts only as the pick or the call:
 * it is a face, so the canvas never falls back to it.
 */
export function resolveWatchedIdentity(input: {
  override: string | null
  called: string | null
  /** A host is live on the stage: their call directs it. Without one, a live Live show comes before a left-over call. */
  hostOnStage?: boolean
  featured: string | null
  cameras: WatchCamera[]
  /** The hosts in Studio mode (`studioFaces`): watched when picked or called, never by default. */
  faces?: WatchCamera[]
}): string | null {
  const choosable = input.faces?.length ? [...input.cameras, ...input.faces] : input.cameras
  const online = (identity: string | null) => (identity && choosable.some((c) => c.identity === identity && c.online) ? identity : null)
  const firstOnline = input.cameras.reduce<WatchCamera | null>(
    (best, c) => (!c.online || (best && cameraOrder(best, c) <= 0) ? best : c),
    null,
  )
  const liveShow = input.cameras.find((c) => c.online && c.sourceRole === 'programme')?.identity ?? null
  return (
    online(input.override) ??
    (input.hostOnStage ? online(input.called) : null) ??
    liveShow ??
    online(input.called) ??
    online(input.featured) ??
    firstOnline?.identity ??
    null
  )
}

/** Where each kind of source sits in the strip: the Live show, the hosts' screens, then the course (a Studio face, never in the strip, sorts before the screens). */
const SOURCE_RANK: Record<WatchSourceRole, number> = { programme: 0, studio: 1, screen: 2, camera: 3 }

/**
 * Live show first, then the hosts' screens, then the cameras along the course by distance, unlocated cameras last, then
 * by name.
 */
export function cameraOrder(a: WatchCamera, b: WatchCamera): number {
  if (a.sourceRole !== b.sourceRole) return SOURCE_RANK[a.sourceRole] - SOURCE_RANK[b.sourceRole]
  const da = a.location?.distance_km ?? Number.POSITIVE_INFINITY
  const db = b.location?.distance_km ?? Number.POSITIVE_INFINITY
  if (da !== db) return da - db
  return a.name.localeCompare(b.name)
}

/** What follows a host's identity in their screen share's source id (`StageMember::SCREEN_SOURCE` in the API). */
const SCREEN_SUFFIX = ':screen'

/** A host's screen share as a source: their connection's identity with `:screen` after it, as a call names it. */
export function screenSource(identity: string): string {
  return `${identity}${SCREEN_SUFFIX}`
}

/**
 * Whose video a source id names and which of their tracks: a host's screen share (`<identity>:screen`), or a
 * participant's camera (any other id: a camera, the Live show, a host's Studio camera).
 */
export function sourceTrack(source: string): { identity: string; screen: boolean } {
  return source.endsWith(SCREEN_SUFFIX) ? { identity: source.slice(0, -SCREEN_SUFFIX.length), screen: true } : { identity: source, screen: false }
}

/** What follows a host's identity when their map is the call (`StageMember::MAP_SOURCE` in the API). */
const MAP_SUFFIX = ':map'

/** A live host's map as a call names it: their connection's identity with `:map` after it. */
export function mapSource(identity: string): string {
  return `${identity}${MAP_SUFFIX}`
}

/**
 * The live host on the stage whose map the call names (docs/features/follow-host-map.md), or null for any other call
 * (a camera, a screen, nothing) and for a map whose host no longer holds a seat here.
 */
export function mapHostOf(called: string | null, members: readonly StageFace[]): StageFace | null {
  if (!called?.endsWith(MAP_SUFFIX)) return null
  const identity = called.slice(0, -MAP_SUFFIX.length)
  return members.find((member) => member.role === 'host' && member.identity === identity) ?? null
}

/** A host's own picture as a source, live while `live` has its id and on its way while only `publishing` does. */
function hostSource(identity: string, name: string, sourceRole: 'studio' | 'screen', live: ReadonlySet<string>, publishing: ReadonlySet<string>): WatchCamera {
  return { identity, name, sourceRole, online: live.has(identity), waiting: publishing.has(identity) && !live.has(identity), location: null }
}

/**
 * The live hosts' shared screens on a stage, as sources for the strip, each while it is shared (it goes when the share
 * stops). `live` and `publishing` are source ids with video now and with video on its way
 * (`useEventLiveKitVideoSources`). Named for the person ("Shane’s screen"); never on the map.
 */
export function hostSources(members: readonly StageFace[], live: ReadonlySet<string>, publishing: ReadonlySet<string>): WatchCamera[] {
  return members.flatMap((member) => {
    const screen = screenSource(member.identity)
    if (member.role !== 'host' || !(live.has(screen) || publishing.has(screen))) return []
    const name = nameOrHandle(member.name, member.handle, 'Host')
    return [hostSource(screen, `${visibleText(name) || name}’s screen`, 'screen', live, publishing)]
  })
}

/**
 * The hosts in Studio mode, as pictures the canvas can show: each is a 16:9 face (StageFaces), never a tile in the
 * strip, and becomes the main video only when picked (a tap on the face) or called (`resolveWatchedIdentity`'s
 * `faces`). Named for the person; never on the map.
 */
export function studioFaces(members: readonly StageFace[], live: ReadonlySet<string>, publishing: ReadonlySet<string>): WatchCamera[] {
  return members
    .filter((member) => member.role === 'host' && member.studio)
    .map((member) => hostSource(member.identity, nameOrHandle(member.name, member.handle, 'Host'), 'studio', live, publishing))
}

/** How many people can be live on a stage at once (`Stage::SEATS` in services/streambridge/app/models/stage.rb). */
export const STAGE_SEATS = 4

/**
 * Every seat is taken by someone else. A user already live (in another tab) still has their seat: going live here
 * moves it, as `Stage#go_live!` allows.
 */
export function stageFull(members: StageFace[], userId: string | null | undefined): boolean {
  return members.length >= STAGE_SEATS && !members.some((member) => member.userId === userId)
}

/**
 * Where the event is in its schedule, from the payload's `starts_at` and `ends_at`, for what the watch screen says with
 * nothing live: `before` its start, or with no start it can read (warming up); `during`, from its start until its end
 * has passed, or with no end (a quiet stretch); `after`, once its end has passed (all finished). The API's
 * `event.status` is only live or idle, so it can't say any of this.
 */
export type EventPhase = 'before' | 'during' | 'after'

function timeOf(value: string | null | undefined): number | null {
  const time = value ? Date.parse(value) : Number.NaN
  return Number.isFinite(time) ? time : null
}

export function eventPhase(startsAt: string | null | undefined, endsAt: string | null | undefined, now = Date.now()): EventPhase {
  const start = timeOf(startsAt)
  const end = timeOf(endsAt)
  if (end !== null && end < now) return 'after'
  if (start !== null && start <= now) return 'during'
  return 'before'
}

/** The longest delay `setTimeout` keeps (about 24.8 days); a longer one fires at once. */
export const MAX_TIMER_MS = 2 ** 31 - 1

/**
 * How long until `eventPhase` next changes (the start, or a millisecond past the end), capped at `MAX_TIMER_MS` so a
 * far-off boundary is waited for in steps; null when nothing is left to change.
 */
export function msUntilPhaseChange(startsAt: string | null | undefined, endsAt: string | null | undefined, now = Date.now()): number | null {
  const start = timeOf(startsAt)
  const end = timeOf(endsAt)
  const boundaries = [start !== null && start > now ? start : null, end !== null && end >= now ? end + 1 : null].filter(
    (time): time is number => time !== null,
  )
  if (boundaries.length === 0) return null
  return Math.min(Math.min(...boundaries) - now, MAX_TIMER_MS)
}

/**
 * The phone's first tab: Chat when the stage has a live host, Map when nobody is hosting, unless the map has nothing to
 * show, when an empty Map tab would greet the viewer: Chat then too. Nothing to show is no map at all (no course, no
 * located camera in the payload), or a map with nothing to frame (`unframed`: no course, and no located camera shown,
 * as a live-only event's whose located cameras are all offline). While the stage's call is a host's map (`mapCalled`),
 * the Map, where it moves with theirs.
 */
export function openingTab({
  hasLiveHost,
  hasMap,
  unframed = false,
  mapCalled = false,
}: {
  hasLiveHost: boolean
  hasMap: boolean
  unframed?: boolean
  mapCalled?: boolean
}): WatchTab {
  if (mapCalled && hasMap) return 'map'
  return hasLiveHost || !hasMap || unframed ? 'chat' : 'map'
}

const audienceFormat = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 })

/** How many are watching, short in the viewer's locale (18, 1.2K): the page's header and the chat's head say it alike. */
export function formatAudience(count: number): string {
  return audienceFormat.format(count)
}

const courseLengthShort = new Intl.NumberFormat(undefined, { style: 'unit', unit: 'kilometer', unitDisplay: 'short', maximumFractionDigits: 1 })
const courseLengthLong = new Intl.NumberFormat(undefined, { style: 'unit', unit: 'kilometer', unitDisplay: 'long', maximumFractionDigits: 1 })

/**
 * The course's length (the primary route's `distance_km`) in the viewer's locale, to a tenth of a kilometre: `short`
 * for the event's bar ("86.4 km"), `long` for screen readers ("86.4 kilometers"). Null without a length worth showing.
 */
export function formatCourseLength(km: number | null | undefined): { short: string; long: string } | null {
  if (typeof km !== 'number' || !Number.isFinite(km) || km <= 0) return null
  return { short: courseLengthShort.format(km), long: courseLengthLong.format(km) }
}

const courseLengthPlain = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 })

/**
 * An event's several courses' lengths as the range they span, in the viewer's locale: `short` for the event's bar
 * ("10–42 km"), `long` for screen readers ("10 to 42 kilometers"). Null without two lengths worth showing; courses all
 * of one length are that length.
 */
export function formatCourseRange(kms: readonly (number | null)[]): { short: string; long: string; several: true } | null {
  const lengths = kms.filter((km): km is number => typeof km === 'number' && Number.isFinite(km) && km > 0)
  if (lengths.length < 2) return null
  const shortest = Math.min(...lengths)
  const longest = Math.max(...lengths)
  if (courseLengthPlain.format(shortest) === courseLengthPlain.format(longest)) {
    return { short: courseLengthShort.format(longest), long: courseLengthLong.format(longest), several: true }
  }
  return {
    short: `${courseLengthPlain.format(shortest)}–${courseLengthShort.format(longest)}`,
    long: `${courseLengthPlain.format(shortest)} to ${courseLengthLong.format(longest)}`,
    several: true,
  }
}

/** The longest chat message the API accepts (`ChatMessage` validates 1–500 characters, counted in code points). */
export const CHAT_MESSAGE_MAX_LENGTH = 500

/** The newest confirmed messages a stage's chat keeps (and its panel shows). */
export const CHAT_MAX_MESSAGES = 200

/** Whether this viewer may chat now, from the stage payload's viewer or a refused post (see `ChatBlocked`). */
export function chatBlock(blocked: ChatBlocked | null | undefined, now = new Date()): ChatBlock {
  if (!blocked) return { kind: 'none' }
  if (blocked.removed) return { kind: 'removed' }
  const end = blocked.until ? new Date(blocked.until) : null
  if (!end || Number.isNaN(end.getTime()) || end <= now) return { kind: 'none' }
  return { kind: 'timeout', until: end }
}

// The server's ChatMessage body normalisation (services/streambridge: ChatMessage `normalizes :body`, VisibleText).
const UNPAIRED_SURROGATE = /\p{Cs}/gu
const LINE_BREAK = /\r\n|[\r\u0085\u2028\u2029]/g
const SUBDIVISION_FLAG_START = '\u{1F3F4}'
/** A subdivision flag (kept whole), or one control or format character or filler (dropped). */
const FLAG_OR_INVISIBLE = /\u{1F3F4}[\u{E0061}-\u{E007A}]+\u{E007F}|[\p{Cc}\p{Cf}\u115F\u1160\u2800\u3164\uFFA0]/gu
/** What stays of the format and control characters: the joiners. */
const JOINERS: ReadonlySet<string> = new Set(['\u200C', '\u200D'])
/** What stays of them in a body: the joiners, and its lines and tabs. */
const KEPT_IN_BODY: ReadonlySet<string> = new Set([...JOINERS, '\n', '\t'])
const JOINER_RUN = /[\u200C\u200D]{2,}/g
const BLANK_LINES = /\n(?:[\t\p{Zs}]*\n)+/gu
const SURROUNDING_SPACE = /^\s+|\s+$/g
const VISIBLE = /[^\s\p{M}\u200C\u200D]/u

/** `text` without the control and format characters and fillers, but subdivision flags and `kept` (one joiner from each run). */
function withoutInvisible(text: string, kept: ReadonlySet<string>): string {
  return text
    .replace(FLAG_OR_INVISIBLE, (match) => (match.startsWith(SUBDIVISION_FLAG_START) || kept.has(match) ? match : ''))
    .replace(JOINER_RUN, (run) => run[0])
}

/**
 * A chat body as the server saves it: every line break a newline, without what draws nothing (control and format
 * characters such as direction overrides and zero-width spaces, the Hangul and Braille fillers, unpaired surrogates)
 * except the joiners (one from each run), subdivision flags, newlines and tabs; one blank line at most; trimmed.
 */
export function normalizeChatBody(body: string): string {
  return withoutInvisible(body.replace(UNPAIRED_SURROGATE, '').replace(LINE_BREAK, '\n'), KEPT_IN_BODY)
    .replace(BLANK_LINES, '\n\n')
    .replace(SURROUNDING_SPACE, '')
}

/**
 * What draws in `text`, cleaned as the server cleans a body (VisibleText) but on one line: without control and format
 * characters (direction controls, zero-width spaces), the Hangul and Braille fillers and unpaired surrogates, keeping
 * the joiners (one from each run) and subdivision flags; trimmed. '' when nothing in it draws.
 */
export function visibleText(text: string): string {
  const cleaned = withoutInvisible(text.replace(UNPAIRED_SURROGATE, ''), JOINERS).replace(SURROUNDING_SPACE, '')
  return VISIBLE.test(cleaned) ? cleaned : ''
}

/**
 * A person's name to show: as written (trimmed) when anything in it draws, else null, so the caller falls back to the
 * handle. The server keeps a first name of only what draws nothing (`User#first_name` is `name.strip.presence`, and
 * `strip` leaves U+3164, U+200B, U+2800 and the like), which would show as a blank. A name that draws keeps its
 * direction characters: show it in an isolate (`<bdi>`), or clean it with `visibleText` for a sentence.
 */
export function visibleName(name: string | null | undefined): string | null {
  const trimmed = name?.trim()
  return trimmed && visibleText(trimmed) ? trimmed : null
}

/**
 * Who someone is, to show: their name when anything in it draws (`visibleName`, as written), else their `@handle`,
 * else `fallback`. The chat's authors, the stage's faces and its host all say it this way, so a first name of only
 * invisible characters never shows as a blank.
 */
export function nameOrHandle(name: string | null | undefined, handle: string | null | undefined, fallback: string): string {
  return visibleName(name) ?? (handle ? `@${handle}` : fallback)
}

/** Who a sentence names when their name and handle are both gone. */
const SOMEONE = 'Someone'

/**
 * A person's name for a sentence (a toast, a dialog's title, a button's name): their name, else their handle, without
 * what draws nothing (a first name keeps its direction controls; an override would reorder the rest of the sentence).
 */
export function sentenceName(person: { name: string | null; handle: string | null }): string {
  return visibleText(nameOrHandle(person.name, person.handle, SOMEONE)) || SOMEONE
}

/** What someone's initials come from: what draws in their name, else their handle; null with neither. */
export function initialsSource(name: string | null | undefined, handle: string | null | undefined): string | null {
  const shown = visibleName(name)
  return shown ? visibleText(shown) : handle || null
}

/** A chat card, as the chat holds it: an invite, joined or left row with the person it is about. */
export type ChatCardMessage = ChatMessage & { kind: ChatCardKind; subject: ChatPerson }

const CARD_KINDS: ReadonlySet<string> = new Set<ChatCardKind>(['invite', 'joined', 'left'])

/** A row that is a card (`invite`, `joined` or `left`) rather than someone's message. A row with no kind is a message. */
export function isChatCard(message: ChatMessage): message is ChatCardMessage {
  return message.kind !== undefined && CARD_KINDS.has(message.kind) && Boolean(message.subject)
}

/**
 * What to post for `text`: the body as the server will save it, cut to 500 code points (the server's count), or ''
 * when nothing in it draws (the server refuses those).
 */
export function chatBody(text: string): string {
  let body = normalizeChatBody(text)
  const points = Array.from(body)
  if (points.length > CHAT_MESSAGE_MAX_LENGTH) body = normalizeChatBody(points.slice(0, CHAT_MESSAGE_MAX_LENGTH).join(''))
  return VISIBLE.test(body) ? body : ''
}

/**
 * A profile's `avatar_url` as an image source. An organisation's logo can be a path the web app serves
 * (`/brands/comrades-logo.svg`), so a relative path resolves against the web origin, never the API's.
 * An absolute http(s) URL is returned as is; nothing, a blank or any other scheme is null.
 */
export function profileAvatarUrl(url: string | null | undefined, origin: string = window.location.origin): string | null {
  const value = url?.trim()
  if (!value) return null
  let resolved: URL
  try {
    resolved = new URL(value, origin)
  } catch {
    return null
  }
  if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return null
  return /^https?:\/\//i.test(value) ? value : resolved.href
}
